import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { map, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { BreadcrumbService } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { ApiResponse, AuthService } from '@card-software/nexus-utilities';
import { DyeingMachine, DyeingMachineStatus, getDyeingMachineStatus } from '@app/models/dyeing-dashboard/dyeing-machine';
import { ProductionCardDyeingCycle } from '@app/models/dyeing-dashboard/production-card-dyeing-cycle';
import { ProductionCard, ProductionCardSearchCommand } from '@app/models/production/production-card';
import { DyeingRoles } from '@app/models/roles/roles-group';
import { DyeingMachineProxyService } from '@app/proxies/production/dyeing-machine.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { ProductionCardDyeingCycleProxyService } from '@app/proxies/production/production-card-dyein-cycle.proxy.service';

@Component({
  selector: 'app-dyeing-machine-details',
  templateUrl: './dyeing-machine-details.component.html',
  styleUrl: './dyeing-machine-details.component.scss',
})
export class DyeingMachineDetailsComponent implements OnInit {
  currentMachine = signal<DyeingMachine | null>(null);
  selectedProductionCard = signal<ProductionCard | null>(null);
  assetId!: string;
  isLoading = signal<boolean>(false);

  // QA Form State
  isApproved = signal<boolean>(true);
  qaComments = new FormControl<string>('');

  // Computed properties
  productionCard = computed<ProductionCard | null>(() => {
    return this.currentMachine()?.currentProductionCard || this.selectedProductionCard() || null;
  });

  machineStatus = computed<DyeingMachineStatus>(() => {
    const machine = this.currentMachine();
    return machine ? getDyeingMachineStatus(machine) : DyeingMachineStatus.StandBy;
  });

  showSearchSection = computed<boolean>(() => {
    return this.machineStatus() === DyeingMachineStatus.StandBy;
  });

  showStartButton = computed<boolean>(() => {
    return (
      this.machineStatus() === DyeingMachineStatus.StandBy &&
      this.selectedProductionCard() !== null &&
      AuthService.hasRole(DyeingRoles.DyeingAdmin)
    );
  });

  showEndCycleButton = computed<boolean>(() => {
    return this.machineStatus() === DyeingMachineStatus.Running && AuthService.hasRole(DyeingRoles.DyeingAdmin);
  });

  showHardStopButton = computed<boolean>(() => {
    return this.machineStatus() === DyeingMachineStatus.Running && AuthService.hasRole(DyeingRoles.DyeingAdmin);
  });

  showQASection = computed<boolean>(() => {
    return this.machineStatus() === DyeingMachineStatus.AwaitingQA && AuthService.hasRole(DyeingRoles.DyeingQualityAdmin);
  });

  showUnloadButton = computed<boolean>(() => {
    return this.machineStatus() === DyeingMachineStatus.Unloading && AuthService.hasRole(DyeingRoles.DyeingAdmin);
  });

  showHardApproveButton = computed<boolean>(() => {
    const machine = this.currentMachine();
    if (this.machineStatus() !== DyeingMachineStatus.Unloading || !AuthService.hasRole(DyeingRoles.DyeingQualityAdmin)) return false;
    const colorApproval = machine?.currentDyeingCycle?.colorStatusApproval;
    return !colorApproval || !colorApproval.isApproved;
  });

  showCommentsField = computed<boolean>(() => {
    return !this.isApproved();
  });

  currentCycleId = computed<string | undefined>(() => {
    return this.currentMachine()?.currentDyeingCycle?.id;
  });

  isFirstRun = computed<boolean>(() => {
    return this.currentMachine()?.currentDyeingCycle?.isFirstRun ?? false;
  });

  showFirstRunWarning = computed<boolean>(() => {
    const hasActiveCycle = this.currentMachine()?.currentDyeingCycle !== undefined;
    return hasActiveCycle && this.isFirstRun();
  });

  workOrderNumber = computed<string | undefined>(() => {
    return this.currentMachine()?.currentWorkOrder?.orderNumber;
  });

  // Expose enum for template
  DyeingMachineStatus = DyeingMachineStatus;

  private readonly productionCardProxyService = inject(ProductionCardsProxyService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dyeingDashboardProxyService = inject(DyeingMachineProxyService);
  private readonly dyeingCycleProxyService = inject(ProductionCardDyeingCycleProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      if (params['assetId']) {
        this.assetId = params['assetId'];
        this.breadcrumbService.setCurrent(this.assetId);
        this.getDyeingMachine(params['assetId']);
      } else {
        GlobalMessageUtil.error('Error', 'No asset ID provided');
        this.redirectDashboard();
      }
    });

    const lang = this.translateService.getDefaultLang();

    switch (lang) {
      case 'es':
        this.qaComments.setValue('Color fuera de estandar');
        break;
      case 'en':
        this.qaComments.setValue('Color out of standard');
        break;
      default:
        this.qaComments.setValue('Color out of standard');
    }
  }

  //#region Get Dyeing Machine Details

  private getDyeingMachine(assetID: string) {
    this.isLoading.set(true);
    this.dyeingDashboardProxyService.getMachineById(assetID).subscribe({
      next: ({ data }) => {
        this.isLoading.set(false);
        if (!data) return;
        this.currentMachine.set(data);
      },
      error: () => {
        this.isLoading.set(false);
        this.redirectDashboard();
      },
    });
  }

  private reloadMachine() {
    this.getDyeingMachine(this.assetId);
  }

  //#endregion

  //#region Production Card AutoComplete

  public fetchProductionCards = (query: string): Observable<ProductionCard[]> => {
    const searchCommand: ProductionCardSearchCommand = {
      productionCardNumber: query,
      assetId: this.assetId,
    };

    return this.productionCardProxyService.search(searchCommand).pipe(map(({ data }) => data ?? []));
  };

  assignmentProductionCards(event: AutoCompleteSelectEvent) {
    const da = event.value as ProductionCard;

    if (da) {
      this.selectedProductionCard.set(da);
    }
  }

  //#endregion

  //#region Cycle Management - Shared Methods

  private confirmAction(message: string, header: string, callback: () => void) {
    this.confirmationService.confirm({
      message,
      header,
      icon: 'pi pi-exclamation-triangle',
      accept: callback,
    });
  }

  private executeCycleAction(action: Observable<ApiResponse<ProductionCardDyeingCycle>>, successMessage: string, onSuccess?: () => void) {
    this.isLoading.set(true);
    action.subscribe({
      next: ({ data }) => {
        this.isLoading.set(false);
        if (data) {
          GlobalMessageUtil.success('Success', successMessage);
          if (onSuccess) onSuccess();
          this.reloadMachine();
        }
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  //#endregion

  //#region Start Cycle

  public confirmStartCycle() {
    const productionCard = this.selectedProductionCard();
    if (!productionCard) {
      GlobalMessageUtil.error('Error', 'No production card selected');
      return;
    }

    this.confirmAction(`Start dyeing cycle for Production Card ${productionCard.productionCardNumber}?`, 'Confirm Start Cycle', () =>
      this.startCycle(),
    );
  }

  private startCycle() {
    const productionCard = this.selectedProductionCard();
    if (!productionCard || !productionCard.id) {
      GlobalMessageUtil.error('Error', 'No production card selected');
      return;
    }

    const action = this.dyeingCycleProxyService.startCycle(productionCard.id, this.assetId);
    this.executeCycleAction(action, 'Cycle started successfully - Machine is now Running', () => {
      this.selectedProductionCard.set(null);
    });
  }

  //#endregion

  //#region End Cycle

  public confirmEndCycle() {
    this.confirmAction('End the current dyeing cycle?', 'Confirm End Cycle', () => this.endCycle());
  }

  private endCycle() {
    const cycleId = this.currentCycleId();
    if (!cycleId) {
      GlobalMessageUtil.error('Error', 'No active cycle found');
      return;
    }

    const action = this.dyeingCycleProxyService.endCycle(cycleId, this.assetId);
    this.executeCycleAction(action, 'Cycle ended successfully - Awaiting QA');
  }

  //#endregion

  //#region Quality Control

  public confirmSubmitQA() {
    const approved = this.isApproved();
    const comments = this.qaComments.value || '';

    // Validate that if not approved, comments are required
    if (!approved && !comments.trim()) {
      GlobalMessageUtil.error('Error', 'Comments are required when rejecting');
      return;
    }

    const message = approved ? 'Approve this dyeing cycle?' : `Reject this dyeing cycle with comments: "${comments}"?`;

    this.confirmAction(message, 'Confirm Quality Control', () => this.submitQualityControl());
  }

  private submitQualityControl() {
    const cycleId = this.currentCycleId();
    if (!cycleId) {
      GlobalMessageUtil.error('Error', 'No active cycle found');
      return;
    }

    const approved = this.isApproved();
    const comments = this.qaComments.value || '';

    const action = this.dyeingCycleProxyService.qualityControlApproval(cycleId, approved, comments || undefined);
    this.executeCycleAction(action, 'Quality control submitted successfully - Ready for Unloading', () => {
      this.isApproved.set(true);
      this.qaComments.setValue('');
    });
  }

  //#endregion

  //#region Unload Cycle

  public confirmUnload() {
    this.confirmAction('Unload the dyeing machine?', 'Confirm Unload', () => this.unloadCycle());
  }

  private unloadCycle() {
    const cycleId = this.currentCycleId();
    if (!cycleId) {
      GlobalMessageUtil.error('Error', 'No active cycle found');
      return;
    }

    const action = this.dyeingCycleProxyService.unloadCycle(cycleId);
    this.executeCycleAction(action, 'Cycle unloaded successfully - Machine is now in StandBy');
  }

  //#endregion

  //#region Hard Approve

  public confirmHardApprove() {
    this.confirmAction(
      'Are you sure you want to hard approve this cycle? This will override the normal approval process.',
      'Confirm Hard Approve',
      () => this.hardApprove(),
    );
  }

  private hardApprove() {
    const cycleId = this.currentCycleId();
    if (!cycleId) {
      GlobalMessageUtil.error('Error', 'No active cycle found');
      return;
    }

    const action = this.dyeingCycleProxyService.hardApprove(cycleId);
    this.executeCycleAction(action, 'Cycle hard approved successfully');
  }

  //#endregion

  //#region Hard Stop

  public confirmHardStop() {
    this.confirmAction(
      'Are you sure you want to hard stop this cycle? This will end the cycle and reject quality control.',
      'Confirm Hard Stop',
      () => this.hardStop(),
    );
  }

  private hardStop() {
    const cycleId = this.currentCycleId();
    if (!cycleId) {
      GlobalMessageUtil.error('Error', 'No active cycle found');
      return;
    }

    const action = this.dyeingCycleProxyService.hardStop(cycleId);
    this.executeCycleAction(action, 'Cycle hard stopped successfully');
  }

  //#endregion

  redirectDashboard() {
    this.router.navigate(['/dyeing/dashboard']);
  }
}
